<script setup lang="ts">
import { computed } from 'vue';
import {
  ColDef,
  ExportParams,
  FilterModel,
  IRowNode,
  LoadSuccessParams,
  SortModelItem,
} from 'ag-grid-enterprise';
import { AgTableServer } from '@/modules/common/components/ag-table';
import * as cols from '@/modules/common/components/ag-table/columns/marketplace';
import { FrontendOrder } from '@/connect/gen/modules/apiengine/services/oms/oms_pb';
import { OmsOrderStatus } from '@/connect/gen/consts/omsconsts_pb';
import { columnKeysExcl } from '@/modules/common/components/ag-table/export';
import { store as vuex } from '@/store/store';

const counterpartyChoiceEnabledCols = ['counterparties'];
const settlementTypeChoiceEnabledCols = ['settlementType'];

const omittedByDefaultHeaders = ['companyId'];

const props = withDefaults(
  defineProps<{
    omitHeaders?: string[];
    extraHeaders?: string[];
    actions?: string[];
    queryData: (config: {
      page: number;
      pageSize: number;
      sort: SortModelItem;
      filter: FilterModel;
      signal: AbortSignal;
    }) => Promise<LoadSuccessParams | undefined>;
    selectedItems?: FrontendOrder[];
    showAll?: boolean;
    showSelect?: boolean;
    sort: SortModelItem;
  }>(),
  {
    omitHeaders: () => [],
    extraHeaders: () => [],
    actions: () => ['view', 'edit', 'route', 'unroute', 'cancel'],
    selectedItems: () => [],
    omitTopOfBookFiltersAndSorting: false,
    showSelect: true,
  }
);

const emit = defineEmits<{
  (event: 'view-order', orderRef: string): void;
  (event: 'edit-order', order: FrontendOrder): void;
}>();

const defaultExportParams = computed(
  (): ExportParams<never> => ({
    fileName: 'orders-export',
    skipPinnedTop: true,
    skipPinnedBottom: true,
    columnKeys: columnKeysExcl(columnDefs.value, 'actions'),
  })
);

const columnDefs = computed((): ColDef[] => {
  const filterConfig = { filter: true, omitTopOfBookFiltersAndSorting: props.showAll };
  return [
    cols.checkbox(),
    cols.status({
      viewOrder,
      ...filterConfig,
    }),
    cols.company(filterConfig),
    cols.side(filterConfig),
    cols.ticker(filterConfig),
    cols.orderIntent({ filter: true }),
    cols.openQuantity(filterConfig),
    cols.percentagePar({ hiddenByDefault: true, ...filterConfig }),
    cols.orderUnitPrice(filterConfig),
    cols.openMarketValue(filterConfig),
    cols.fee(filterConfig),
    cols.rebate(filterConfig),
    cols.ioiQuantity(filterConfig),
    cols.firmQuantity(filterConfig),
    cols.execQty(filterConfig),
    cols.cusip(filterConfig),
    cols.isin(filterConfig),
    cols.securityCategory(filterConfig),
    cols.securityType(filterConfig),
    cols.securityName(filterConfig),
    cols.updateTime(filterConfig),
    cols.agreements(filterConfig),
    cols.priceChange(filterConfig),
    cols.avgExecFee(filterConfig),
    cols.avgExecRebate(filterConfig),
    cols.totalQuantity(filterConfig),
    cols.timeInForce(filterConfig),
    cols.goodTillTime(filterConfig),
    cols.minQuantity(filterConfig),
    cols.createTime(filterConfig),
    cols.createdBy(filterConfig),
    cols.lastUpdatedBy(filterConfig),
    cols.bboSpreadPctRebate(filterConfig),
    cols.bboQuantity(filterConfig),
    cols.bboFee(filterConfig),
    cols.bboSpreadVsFee(filterConfig),
    cols.bboSpreadVsRebate(filterConfig),
    cols.bboAgreements(filterConfig),
    cols.actions({
      actions: props.actions,
      viewOrder,
      editOrder: (order: FrontendOrder) => emit('edit-order', order),
    }),
  ].filter(
    (colDef) =>
      (props.showSelect || colDef.colId !== 'checkbox') &&
      !props.omitHeaders.includes(colDef.colId as string) &&
      (!omittedByDefaultHeaders.includes(colDef.colId as string) ||
        props.extraHeaders.includes(colDef.colId as string)) &&
      (vuex.state.clientConfig?.orderbookCounterpartyChoiceEnabled ||
        !counterpartyChoiceEnabledCols.includes(colDef.field as string)) &&
      (vuex.state.clientConfig?.orderbookSettlementTypeChoiceEnabled ||
        !settlementTypeChoiceEnabledCols.includes(colDef.field as string))
  );
});

function viewOrder(orderRef: string): void {
  emit('view-order', orderRef);
}

function getRowId(order: FrontendOrder): string {
  return order.orderRef;
}

function isRowSelectable(rowNode: IRowNode<FrontendOrder>): boolean {
  return !isStatusDisabled(rowNode.data?.orderStatus);
}

// if the order status is one of the fixed array items return true
// in the template the checkbox :disabled will be true
function isStatusDisabled(status: OmsOrderStatus | undefined): boolean {
  const disabledStatuses: OmsOrderStatus[] = [
    OmsOrderStatus.CANCELED,
    OmsOrderStatus.EXPIRED,
    OmsOrderStatus.TERMINATED,
    OmsOrderStatus.FILLED,
  ];

  return status !== undefined && disabledStatuses.includes(status);
}

// for tests only
defineExpose({
  viewOrder,
  columnDefs,
});
</script>

<template>
  <AgTableServer
    :column-defs="columnDefs"
    :default-export-params="defaultExportParams"
    :get-row-id="getRowId"
    :is-row-selectable="isRowSelectable"
    :page="1"
    :page-size="1000"
    :query-data="queryData"
    :selected-items="selectedItems"
    :sort="sort"
    table-id="MarketplaceOrdersTable"
    v-on="$listeners"
  />
</template>
